import { ApplicationHttpClient } from '@app/helpers/custom-http-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { UserUpdateInfo } from '@app/shared/models/user/user.update';
import { User } from '@app/shared/models/user/user';
import {
  HttpResult,
  HttpResultWithPagination,
  MetaData,
  HttpResultWithMetaAndNetworksPagination,
} from '@app/shared/models/http-result';
import { map } from 'rxjs/operators';
import { Company, CompanyUser } from '@app/shared/models/company';
import { Brand } from '@app/shared/models/brand';
import { OnboardingDirection } from '@app/shared/models/user/onboarding.direction.model';
import { GeoKLocation } from '@app/shared/models/geo-k-location';
import { Review } from '@app/modules/reviews/models/review';
import { ReviewStats } from '@app/shared/models/review-stats';
import { UserAccess } from '@app/shared/models/user/user-access';
import { Pagination, PagingRequest } from '@app/shared/models/paging.class';
import { FilterRequest } from '@app/shared/models/filter';
import { LocationStats } from '@app/shared/models/location-stats';
import { PlanSubscription } from '@app/shared/models/user/plan-subscription';
import { IPostResponse } from '@app/modules/social-posting/models/post.interface';
import { NetworksEnum } from '@app/modules/addons/models/reviews-plus.model';
import {
  Check2faResult,
  Initiate2faResult,
  Verify2faResult,
} from '@app/shared/models/user/login.user.model';
import { GoogleUpdateQueueItem } from '@app/shared/models/google-update-queue';

@Injectable()
export class UserService {
  private usersURL = '/users';
  reviewsDashboard$: BehaviorSubject<ReviewStats> =
    new BehaviorSubject<ReviewStats>({} as ReviewStats);

  constructor(private http: ApplicationHttpClient) { }

  /**
   * Public pre-login: whether this account has 2FA (must not send auth;
   * uses bypass-interceptor like password reset).
   */
  public check2fa(email: string): Observable<Check2faResult> {
    const url = this.usersURL + '/check-2fa';
    return this.http
      .post<unknown>(url, { email }, {
        headers: new HttpHeaders({ 'bypass-interceptor': 'true' }),
      })
      .pipe(
        map((body) => this.parseCheck2faResponse(body))
      );
  }

  /**
   * Authenticated: verify current user's 2FA status.
   * Returns 401 if session is invalid, which triggers logout.
   */
  public verifyAuthenticated2faStatus(): Observable<Check2faResult> {
    const url = this.usersURL + '/check-2fa';
    return this.http
      .get<unknown>(url)
      .pipe(
        map((body) => this.parseCheck2faResponse(body))
      );
  }

  /**
   * Public pre-login: verify password and send OTP for users with email 2FA.
   */
  public initiate2fa(
    email: string,
    password: string
  ): Observable<Initiate2faResult> {
    const url = this.usersURL + '/2fa/initiate';
    return this.http
      .post<unknown>(
        url,
        { email, password },
        {
          headers: new HttpHeaders({ 'bypass-interceptor': 'true' }),
        }
      )
      .pipe(map((body) => this.parseInitiate2faResponse(body)));
  }

  /**
   * Public pre-login: verify OTP code and receive a Firebase custom token.
   * Uses bypass-interceptor because no Firebase ID token exists yet.
   */
  public verify2fa(
    sessionId: string,
    code: string
  ): Observable<Verify2faResult> {
    const url = this.usersURL + '/2fa/verify';
    return this.http
      .post<unknown>(
        url,
        { sessionId, code },
        { headers: new HttpHeaders({ 'bypass-interceptor': 'true' }) }
      )
      .pipe(map((body) => this.parseVerify2faResponse(body)));
  }

  /**
   * Public pre-login: resend OTP code for active 2FA session.
   * Uses bypass-interceptor because no Firebase ID token exists yet.
   */
  public resend2fa(sessionId: string): Observable<void> {
    const url = this.usersURL + '/2fa/resend';
    return this.http
      .post<void>(
        url,
        { sessionId },
        { headers: new HttpHeaders({ 'bypass-interceptor': 'true' }) }
      );
  }

  private parseVerify2faResponse(body: unknown): Verify2faResult {
    if (body == null || typeof body !== 'object') {
      throw new Error('Invalid 2FA verify response');
    }
    const root = body as Record<string, unknown>;
    const nested = root['data'];
    const source =
      nested != null && typeof nested === 'object'
        ? (nested as Record<string, unknown>)
        : root;
    const token =
      source['token'] ?? source['firebaseToken'] ?? source['customToken'];
    if (typeof token !== 'string' || !token) {
      throw new Error('Missing Firebase custom token in verify response');
    }
    return { firebaseCustomToken: token };
  }

  private parseCheck2faResponse(body: unknown): Check2faResult {
    if (body == null || typeof body !== 'object') {
      return { twoFactorRequired: false };
    }
    const root = body as Record<string, unknown>;
    const nested = root['data'];
    const source =
      nested != null && typeof nested === 'object'
        ? (nested as Record<string, unknown>)
        : root;
    const v =
      source['twoFactorRequired'] ??
      source['is2faRequired'] ??
      source['is_2fa_required'] ??
      source['required'] ??
      source['mfaRequired'] ??
      source['isMfaEnabled'] ??
      source['twoFaEnabled'] ??
      source['two_fa_enabled'] ??
      source['enabled'];
    const twoFaType =
      source['two_fa'] ??
      source['twoFa'] ??
      source['twoFaType'] ??
      source['two_fa_type'] ??
      source['mfaType'] ??
      source['type'];

    const hasEmail2fa =
      typeof twoFaType === 'string' && twoFaType.toLowerCase() === 'email';

    return { twoFactorRequired: v === true || hasEmail2fa };
  }

  private parseInitiate2faResponse(body: unknown): Initiate2faResult {
    if (body == null || typeof body !== 'object') {
      return { twoFactorRequired: true };
    }

    const root = body as Record<string, unknown>;
    const nested = root['data'];
    const source =
      nested != null && typeof nested === 'object'
        ? (nested as Record<string, unknown>)
        : root;

    const challengeIdCandidate =
      source['sessionId'] ??
      source['challengeId'] ??
      source['challenge_id'] ??
      source['requestId'] ??
      source['session_id'];
    const challengeId =
      typeof challengeIdCandidate === 'string' ? challengeIdCandidate : undefined;

    return {
      twoFactorRequired: true,
      challengeId,
    };
  }

  public linkUser(email: string): Observable<void> {
    const postUserURL = this.usersURL + '/link';
    return this.http.post(postUserURL, { email: email });
  }

  public updateUser(
    userId: string,
    userUpdateInfo: UserUpdateInfo
  ): Observable<void> {
    const updateUserURL = this.usersURL + '/me';
    return this.http.put(updateUserURL, userUpdateInfo);
  }

  public getMe(): Observable<User> {
    const getMeUrl = this.usersURL + '/me';
    return this.http.get<HttpResult>(getMeUrl).pipe(map((it) => it.data));
  }

  public getMyCompany(): Observable<Company> {
    const getMyCompanyUrl = this.usersURL + '/me/company';
    return this.http
      .get<HttpResult>(getMyCompanyUrl)
      .pipe(map((it) => it.data));
  }

  public getMyBrands(): Observable<Brand[]> {
    const getMyBrandsUrl = this.usersURL + '/me/brands';
    return this.http.get<HttpResult>(getMyBrandsUrl).pipe(map((it) => it.data));
  }

  public getMySubscription(): Observable<PlanSubscription> {
    const getMySubscriptionUrl = this.usersURL + '/me/subscription';
    return this.http
      .get<HttpResult>(getMySubscriptionUrl)
      .pipe(map((it) => it.data));
  }

  public getOnboardingDirection(): Observable<OnboardingDirection> {
    const getOnboardingDirectionUrl =
      this.usersURL + '/me/onboarding-directions';
    return this.http.get<HttpResult>(getOnboardingDirectionUrl).pipe(
      map((it: HttpResult) => {
        return {
          ...(it.data as NonNullable<object>),
          message: it.message,
        } as OnboardingDirection;
      })
    );
  }

  public getMyLocations(
    pagingRequest: PagingRequest,
    filters?: FilterRequest[],
    selectMode: string | null = null
  ): Observable<{
    items: GeoKLocation[];
    pagination: Pagination;
  }> {
    const getMyLocationsUrl = this.usersURL + '/me/locations';
    const queryParams: string[] = [];

    if (pagingRequest.page != null) {
      queryParams.push(`page=${pagingRequest.page}`);
    }
    if (pagingRequest.limit != null) {
      queryParams.push(`limit=${pagingRequest.limit}`);
    }
    if (selectMode != null && selectMode !== '') {
      queryParams.push(`selectMode=${encodeURIComponent(selectMode)}`);
    }

    if (!!filters && filters.length !== 0) {
      const tagQuery = filters.find((filter) => filter.property == 'tagId');

      if (tagQuery) {
        queryParams.push(`tags=${JSON.stringify(tagQuery.value)}`);
      }

      filters = filters.filter((filter) => filter.property !== 'tagId');

      if (!!filters && filters.length != 0) {
        queryParams.push(`filter=${JSON.stringify(filters)}`);
      }
    }

    const query = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    return this.http
      .get<HttpResultWithPagination>(getMyLocationsUrl + query)
      .pipe(
        map((it) => {
          return {
            items: it.data,
            pagination: {
              pageIndex: it.page,
              totalCount: it.totalCount,
            },
          };
        })
      );
  }

  public fetchMyReviewsDashboard(): Observable<ReviewStats> {
    return this.reviewsDashboard$.asObservable();
  }

  public getMyReviewDashboard(
    filters?: FilterRequest[]
  ): Observable<ReviewStats> {
    const myReviewsUrl = this.usersURL + '/me/locations/reviews-stats';
    const body = filters?.length ? { filter: filters } : {};

    return this.http.post<HttpResult>(myReviewsUrl, body).pipe(
      map((it) => {
        this.reviewsDashboard$.next(it.data);
        return it.data;
      })
    );
  }

  getReviewsCount(
    filters?: FilterRequest[],
    action?: string
  ): Observable<number> {
    const getReviewsCountUrl = this.usersURL + '/me/locations/reviews-count';
    const body: Record<string, unknown> = {};

    if (filters?.length) {
      body['filter'] = filters;
    }

    if (action) {
      body['action'] = action;
    }

    return this.http
      .post<HttpResultWithPagination>(getReviewsCountUrl, body)
      .pipe(map((it) => it.totalCount));
  }
  public getMyReviews(
    pagingRequest: PagingRequest,
    filters?: FilterRequest[],
    action?: string,
    sorting?: string
  ): Observable<{
    items: Review[];
    networks: NetworksEnum[];
    pagination: Pagination;
    metaData: MetaData;
  }> {
    const getMyReviewsUrl = this.usersURL + '/me/locations/reviews';
    const body: Record<string, unknown> = {
      sort: sorting,
      limit: pagingRequest.limit,
      page: pagingRequest.page
    };

    if (pagingRequest.cursor) {
      body['cursor'] = pagingRequest.cursor;
    }

    if (pagingRequest.nextCursor) {
      body['nextCursor'] = pagingRequest.nextCursor;
    }

    if (pagingRequest.prevCursor) {
      body['prevCursor'] = pagingRequest.prevCursor;
    }

    if (pagingRequest.firstPage) {
      body['firstPage'] = pagingRequest.firstPage;
    }

    if (action) {
      body['action'] = action;
    }

    if (filters?.length) {
      body['filter'] = filters;
    }

    return this.http
      .post<HttpResultWithMetaAndNetworksPagination>(getMyReviewsUrl, body)
      .pipe(
        map((it) => {
          return {
            items: it.data,
            networks: it.networks || [],
            pagination: {
              pageIndex: it.page,
              totalCount: it.totalCount,
              cursor: it.cursor,
              nextCursor: it.nextCursor,
              prevCursor: it.prevCursor,
              firstPage: it.firstPage,
            } as Pagination,
            metaData: it.metadata,
          };
        })
      );
  }

  getUserAccesses(): Observable<UserAccess[]> {
    const getUserAccessesUrl = this.usersURL + '/me/user-accesses';
    return this.http
      .get<HttpResult>(getUserAccessesUrl)
      .pipe(map((it) => it.data));
  }

  public getUser(id: string): Observable<User> {
    const getUserUrl = this.usersURL + '/' + id;
    return this.http.get<HttpResult>(getUserUrl).pipe(map((it) => it.data));
  }

  public updateUserAccess(user: CompanyUser): Observable<CompanyUser> {
    const updateUserWithAccessUrl =
      this.usersURL + '/' + user.id + '/with-access';
    return this.http
      .put<HttpResult>(updateUserWithAccessUrl, user)
      .pipe(map((it) => it.data));
  }

  getMyPosts(filters?: FilterRequest[]): Observable<IPostResponse[]> {
    let query = '';
    if (!!filters && filters.length !== 0) {
      query += `?filter=${JSON.stringify(filters)}`;
    }

    return this.http
      .get<HttpResult>(this.usersURL + '/me/posts' + query)
      .pipe(map((it) => it.data));
  }

  public getMyGoogleUpdateQueues(
    pagingRequest: PagingRequest,
    filters?: FilterRequest[]
  ): Observable<{
    items: GoogleUpdateQueueItem[];
    pagination: Pagination;
  }> {
    const url = this.usersURL + '/me/google-update-queues';
    const body = {
      page: pagingRequest.page,
      limit: pagingRequest.limit,
      filter: filters ?? [],
    };
    return this.http.post<HttpResultWithPagination>(url, body).pipe(
      map((it) => ({
        items: (it.data ?? []) as GoogleUpdateQueueItem[],
        pagination: {
          pageIndex: it.page,
          totalCount: it.totalCount,
        },
      }))
    );
  }

  public approveGoogleUpdateQueue(id: string): Observable<void> {
    const url = this.usersURL + '/me/google-update-queues/approve';
    return this.http.put(url, { ids: [id] });
  }

  public rejectGoogleUpdateQueue(id: string): Observable<void> {
    const url = this.usersURL + '/me/google-update-queues/reject';
    return this.http.put(url, { ids: [id] });
  }

  public getLocationStatus(filters?: FilterRequest[]): Observable<LocationStats> {
    const locationStatusUrl = this.usersURL + '/me/locations/stats';
    let query = '';

    if (!!filters && filters.length !== 0) {
      const tagQuery = filters.find((filter) => filter.property == 'tagId');

      if (tagQuery) {
        query += `?tags=${JSON.stringify(tagQuery.value)}`;
      }

      filters = filters.filter((filter) => filter.property !== 'tagId');

      if (!!filters && filters.length != 0) {
        query += query ? '&' : '?';
        query += `filter=${JSON.stringify(filters)}`;
      }
    }

    return this.http
      .get<HttpResult>(locationStatusUrl + query)
      .pipe(map((it) => it.data));
  }
  public deleteUser(userId: string): Observable<boolean> {
    return this.http
      .delete<HttpResult>(this.usersURL + '/' + userId)
      .pipe(map(() => true));
  }
  public changeActivationStatus(
    userId: string,
    action: string
  ): Observable<boolean> {
    const activationUrl = this.usersURL + '/' + userId + '/status/' + action;
    return this.http
      .put<HttpResult>(activationUrl, { id: userId, action: action })
      .pipe(map(() => true));
  }

  public resendInvitation(email: string): Observable<boolean> {
    const resendInvitationUrl = this.usersURL + '/reinvite/' + email;
    return this.http.get<HttpResult>(resendInvitationUrl).pipe(map(() => true));
  }
}
